// Public repair estimate page (Session ARS, Director decision D91). No login: the unguessable token in the
// link IS the capability. The link is printed on the priced letter or repair quotation the office sends,
// and on its covering email (/estimate/:token). The customer reads the works and the price and approves or
// declines, each confirmed on the page before anything is sent. The office is told either way, and can
// still record an answer that arrives by reply or phone.
//
// Visual treatment: the AcceptQuote world, exactly as AcceptProject reuses it. This page imports
// AcceptQuote.css and uses ONLY its existing classes and colour variables: it adds no colour and no
// stylesheet (the AcceptQuote colour question, D25, is open, so nothing here may add to it). The one inline
// style, on the reason box, reads the stylesheet's own field variables.
//
// House rules: plain English, no markdown, no personal names (ELE signs "For and on behalf of"), the one
// client phone number 0203 576 4760, pounds and pence, no double hyphens and no em dashes.
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PublicRepairEstimateView, PublicRepairEstimateState } from "../../lib/api";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";
import "./AcceptQuote.css";

const PHONE = "0203 576 4760";
const REASON_MAX = 1000;

function gbp(pence: number): string {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABEL: Record<PublicRepairEstimateState, string> = {
  open: "Awaiting your answer",
  approved: "Approved",
  declined: "Declined",
  expired: "Expired",
};

// What the page is doing below the price: waiting for a choice, or asking the customer to confirm one.
type Step = "choose" | "confirm_approve" | "confirm_decline";

export function RepairEstimatePage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PublicRepairEstimateView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "error" | null>(null);
  const [step, setStep] = useState<Step>("choose");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    api.publicRepairEstimate
      .get(token)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((err) => {
        if (!live) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const send = useCallback(
    async (answer: "approve" | "decline") => {
      if (submitting) return;
      setSubmitting(true);
      setMessage(null);
      try {
        const next = answer === "approve" ? await api.publicRepairEstimate.approve(token) : await api.publicRepairEstimate.decline(token, reason.trim() || undefined);
        setView(next);
        setStep("choose");
      } catch (err) {
        // A refusal carries the estimate as it now stands (expired, or already answered): show that, with
        // the server's plain-English sentence. Anything else keeps the page as it was and says so.
        const body = err instanceof ApiError ? err.body : undefined;
        const standing = body && typeof body === "object" ? (body as { estimate?: PublicRepairEstimateView }).estimate : undefined;
        if (err instanceof ApiError && err.status === 404) {
          setLoadError("not_found");
        } else if (standing) {
          setView(standing);
          setStep("choose");
          // The expired panel explains itself; the other refusals are said in the server's own words.
          if (standing.state !== "expired" && err instanceof ApiError) setMessage(err.code);
        } else if (err instanceof ApiError && err.status === 429) {
          setMessage(`Please wait a moment and try again, or call us on ${PHONE}.`);
        } else if (err instanceof ApiError && err.status === 400 && err.code && err.code !== err.name) {
          setMessage(err.code);
        } else {
          setMessage(`Something went wrong. Please try again in a moment, or call us on ${PHONE}.`);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [reason, submitting, token],
  );

  if (loading) {
    return (
      <Shell kind={null}>
        <div className="sec notice" role="status" aria-live="polite">
          <h2>Loading your repair price</h2>
          <p>Just a moment.</p>
        </div>
      </Shell>
    );
  }

  if (loadError || !view) {
    return (
      <Shell kind={null}>
        <div className="sec notice">
          <h2>{loadError === "not_found" ? "We could not find this price" : "Something went wrong"}</h2>
          <p>
            {loadError === "not_found"
              ? "This link may be incorrect, or the price may have been withdrawn. Please call us and we will help."
              : "Please try again in a moment, or call us and we will help."}
          </p>
          <p className="call">Call us on {PHONE}</p>
        </div>
        <Footer />
      </Shell>
    );
  }

  const noun = view.documentKind; // "quotation" or "price", as the customer's own document called itself
  const state = view.state;

  return (
    <Shell kind={noun}>
      <div className="intro">
        <h1>Your repair {noun}</h1>
        <p className="lead">
          {state === "open"
            ? `Please check the works and the price below, then approve or decline this ${noun}.`
            : `The works and the price for repair ${view.reference}.`}
        </p>
      </div>

      <div className="strip">
        <div className="strip-cell">
          <div className="k">Reference</div>
          <div className="v">{view.reference}</div>
          <div className="s">Issued {view.issuedOn}</div>
        </div>
        {view.siteLabel ? (
          <div className="strip-cell">
            <div className="k">Site</div>
            <div className="v">{view.siteLabel}</div>
          </div>
        ) : null}
        {view.unit ? (
          <div className="strip-cell">
            <div className="k">{view.unit.label}</div>
            <div className="v">{view.unit.eleNumber}</div>
          </div>
        ) : null}
        <div className="strip-cell">
          <div className="k">Status</div>
          <div className="v">{STATUS_LABEL[state]}</div>
          <div className="s">{state === "open" ? `Valid until ${view.validUntil}` : state === "expired" ? `Was valid until ${view.validUntil}` : ""}</div>
        </div>
      </div>

      <div className="sec">
        <div className="sec-title">The works</div>
        <div className="narrative">
          {view.works
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line, i) => (
              <p key={i}>{line}</p>
            ))}
        </div>
      </div>

      <div className="sec">
        <div className="sec-title">The price</div>
        <div className="ibd">
          <div className="ibd-row">
            <span className="ibd-nm">Price (ex VAT)</span>
            <span className="ibd-c">{gbp(view.exVatPence)}</span>
          </div>
          <div className="ibd-row">
            <span className="ibd-nm">VAT at {view.vatPercent}%</span>
            <span className="ibd-c">{gbp(view.vatPence)}</span>
          </div>
          <div className="ibd-row ibd-total">
            <span className="ibd-nm">Total (inc VAT)</span>
            <span className="ibd-c">{gbp(view.incVatPence)}</span>
          </div>
        </div>
        <p className="cq-terms-note">
          This is a fixed price for the works described above, including parts. If anything further is found once the work is under way, we will
          price it separately and agree it with you before it is carried out.
        </p>
      </div>

      {message ? (
        <p role="alert" aria-live="assertive" className="cq-error">
          {message}
        </p>
      ) : null}

      {state === "approved" ? (
        <div className="sec notice">
          <div className="ov-tick" aria-hidden="true">
            {"✓"}
          </div>
          <h2>Thank you, this {noun} is approved</h2>
          <p>
            {view.answeredOn ? `This ${noun} was approved on ${view.answeredOn}. ` : ""}We will be in touch to arrange a convenient date for the works.
          </p>
          <p className="call">Any questions? Call us on {PHONE}</p>
        </div>
      ) : state === "declined" ? (
        <div className="sec notice">
          <h2>This {noun} has been declined</h2>
          <p>
            {view.answeredOn ? `This ${noun} was declined on ${view.answeredOn}. ` : ""}Thank you for letting us know. If you change your mind, or would
            like to talk it through, please call us.
          </p>
          <p className="call">Call us on {PHONE}</p>
        </div>
      ) : state === "expired" ? (
        <>
          <div className="sec notice">
            <h2>This {noun} has expired</h2>
            <p>This {noun} was valid until {view.validUntil}, so it can no longer be approved online. Please call us for an updated price.</p>
            <p className="call">Call us on {PHONE}</p>
          </div>
          <div className="sec">
            {step === "confirm_decline" ? (
              <DeclineConfirm noun={noun} reason={reason} setReason={setReason} submitting={submitting} onDecline={() => send("decline")} onBack={() => setStep("choose")} />
            ) : (
              <>
                <p className="sec-note">If you do not wish to go ahead, you can still let us know here.</p>
                <div className="cq-modal-buttons">
                  <button type="button" className="cq-info-btn" onClick={() => setStep("confirm_decline")}>
                    Decline this {noun}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="sec summary">
          <div className="sec-title">Your answer</div>
          <p className="sec-note">
            What happens next: once you approve, we will be in touch to arrange a convenient date for the works. We invoice the agreed price when the
            works are complete. You can also reply to our email or call us on {PHONE}.
          </p>
          {step === "confirm_approve" ? (
            <div>
              <p>
                <strong>Approve this {noun}?</strong> You are asking East London Elevators to carry out the works described above for{" "}
                {gbp(view.incVatPence)} including VAT.
              </p>
              <button type="button" className="confirm" onClick={() => send("approve")} disabled={submitting}>
                {submitting ? "Sending your approval..." : `Yes, approve this ${noun}`}
              </button>
              <div className="cq-modal-buttons">
                <button type="button" className="cq-info-btn" onClick={() => setStep("choose")} disabled={submitting}>
                  Go back
                </button>
              </div>
            </div>
          ) : step === "confirm_decline" ? (
            <DeclineConfirm noun={noun} reason={reason} setReason={setReason} submitting={submitting} onDecline={() => send("decline")} onBack={() => setStep("choose")} />
          ) : (
            <div>
              <button type="button" className="confirm" onClick={() => setStep("confirm_approve")}>
                Approve this {noun}
              </button>
              <div className="cq-modal-buttons">
                <button type="button" className="cq-info-btn" onClick={() => setStep("confirm_decline")}>
                  Decline this {noun}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Footer />
    </Shell>
  );
}

// The decline confirmation: an optional reason, then the decline itself or a way back. The reason is sent
// as plain text and is never shown back on this page.
function DeclineConfirm(props: {
  noun: string;
  reason: string;
  setReason: (s: string) => void;
  submitting: boolean;
  onDecline: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <p>
        <strong>Decline this {props.noun}?</strong> We will not carry out these works.
      </p>
      <div className="cq-commence">
        <label htmlFor="estimate-decline-reason">Would you like to tell us why? (optional)</label>
        <textarea
          id="estimate-decline-reason"
          value={props.reason}
          onChange={(e) => props.setReason(e.target.value)}
          maxLength={REASON_MAX}
          rows={4}
          style={{
            width: "100%",
            padding: "12px 14px",
            border: "1px solid var(--cq-field-border)",
            borderRadius: 10,
            fontFamily: "inherit",
            fontSize: 15,
            color: "var(--cq-ink-strong)",
            background: "var(--cq-field-bg)",
            boxSizing: "border-box",
          }}
        />
        <p className="cq-commence-note">Up to 1,000 characters.</p>
      </div>
      <div className="cq-modal-buttons">
        <button type="button" className="cq-info-btn" onClick={props.onDecline} disabled={props.submitting}>
          {props.submitting ? "Sending..." : `Yes, decline this ${props.noun}`}
        </button>
        <button type="button" className="cq-info-btn" onClick={props.onBack} disabled={props.submitting}>
          Go back
        </button>
      </div>
    </div>
  );
}

// The page shell: the dark header band above the centred content column on the light canvas, shared with
// the maintenance accept page and the project acceptance page through AcceptQuote.css.
function Shell({ kind, children }: { kind: "quotation" | "price" | null; children: ReactNode }) {
  return (
    <div className="cq">
      <div className="cust-hdr">
        <div className="brand">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <small>{kind === "quotation" ? "Repair quotation" : "Repair price"}</small>
        </div>
        <div className="hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
          <br />
          eastlondonelevators.co.uk
        </div>
      </div>
      <div className="wrap">{children}</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="ftr">
      <p>For and on behalf of East London Elevators.</p>
      <p className="co">East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      {/* Session ASQ (Stream G, Item 5 review, F5): the privacy notice link, only once the notice is published. The
          footer sits on the page's LIGHT canvas, so it takes the page's own purple ink, as the quote page's does. */}
      <PrivacyNoticeLink className="co" color="var(--cq-purple-text)" />
      <div className="cq-footbar cq-noprint">
        <p>East London Elevators Ltd &middot; Company No. 15429089 &middot; VAT GB493339461</p>
        <p>Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
        <p>
          <b>{PHONE}</b> &middot; <a href="mailto:info@eastlondonelevators.co.uk">info@eastlondonelevators.co.uk</a> &middot; eastlondonelevators.co.uk
        </p>
      </div>
    </footer>
  );
}
